import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass, treatsSeveral } from '../skillUsage';

export const logoWallAssembleSkill: CodeoSkill = {
    name: SKILL_NAMES.logoWallAssemble,
    kind: 'codeo',
    description:
        'Fill a wall of evenly spaced tiles in a scattered order so the whole set reads as ' +
        'volume — for customer logos, a long roster, coverage, or any "and many more" claim.',
    checkLooksFor: 'a repeated tile class, revealed by a stagger or a per-tile loop',
    documentUsesSkill: check((script, markup) => repeatsAClass(markup) && treatsSeveral(script)),
    instructions: `# Logo Wall Assemble

A regular grid of small tiles fills in, but not row by row: the tiles arrive in a scattered order until the wall is complete. The scatter is what sells volume — a wall that fills left-to-right reads as a list being enumerated, while one that fills all over reads as *many*.

Use this when the count is the argument. If the individual items matter, they need to be big enough to read, which means fewer of them and \`${SKILL_NAMES.cardGridAssemble}\` instead.

## Markup

\`\`\`html
<div id="wall" style="position: absolute; left: 260px; top: 240px; width: 1400px; display: grid; grid-template-columns: repeat(6, 1fr); gap: 22px;">
  <div class="tile" style="height: 120px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); font-family: var(--l5-font-0), sans-serif; font-size: 26px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Northwind</div>
  <div class="tile" style="height: 120px; border-radius: 14px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); font-family: var(--l5-font-0), sans-serif; font-size: 26px; font-weight: 700; color: var(--l5-color-0, #ffffff);">Contoso</div>
  <!-- 22 more tiles -->
</div>
\`\`\`

Label the wall as one asset, not each tile: the user thinks of it as "the logo wall", and 24 labels would be noise.

## Timeline code

The scatter order has to be deterministic — the renderer may draw frames out of order and in a fresh context, so \`Math.random()\` would produce a different wall every frame. Derive the order arithmetically instead.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const tileEls = Array.from(document.querySelectorAll('.tile'));
const FILL_WINDOW = 1.9;

// A large step coprime with the tile count walks every index exactly once in a
// scattered order — the same order on every render.
const STEP = 7;
const order = new Map();
let cursor = 0;
for (let placed = 0; placed < tileEls.length; placed += 1) {
    order.set(tileEls[cursor], placed);
    cursor = (cursor + STEP) % tileEls.length;
}

gsap.set(tileEls, { scale: 0.86, opacity: 0 });

tileEls.forEach((tile) => {
    const at = 0.25 + (order.get(tile) / tileEls.length) * FILL_WINDOW;
    tl.to(tile, { scale: 1, opacity: 1, duration: 0.42, ease: 'power3.out' }, at);
});

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Scatter character**: \`STEP\` must be coprime with the tile count or the walk revisits a subset and leaves gaps. For 24 tiles, 5, 7, 11 and 13 all work; 6 and 8 do not.
- **Radial fill**: instead of the coprime walk, sort tiles by distance from the wall's center and stagger on that — reads as a spreading wave rather than a scatter.
- **Density over legibility**: at 40+ tiles drop the labels to marks or plain blocks and shorten \`FILL_WINDOW\`; the wall becomes a texture that means "a lot".
- **A named few**: hold three tiles back and land them last at full opacity while the rest sit at \`opacity: 0.45\` — the wall becomes context and those three become the point.
- **Real logos**: inline \`<svg>\` marks at a fixed height inside each tile. Keep them one color (\`fill: currentColor\`) so the wall stays a single visual object rather than 24 competing brands.

## Pitfalls

- Never use \`Math.random()\` for the order. Every frame is a pure function of timeline progress, and a random order re-rolls per frame — the wall would flicker.
- Tiles are \`scale\`-tweened, so no tile may carry a CSS \`transform\`.
- Keep \`FILL_WINDOW\` under about 2s. A wall that takes four seconds to fill has stopped being an establishing beat and become the whole scene.
- Don't stagger with \`stagger: 0.04\` in DOM order and call it scattered — DOM order is exactly the row-by-row fill this technique exists to avoid.
- After the wall completes, something must still move. A completed wall is a static image, and a static tail is where a graphic dies.
- Don't put a headline *and* a subhead above the wall. The wall is already the second element; a third makes the frame a slide.
- Reproducing real company logos means reproducing their trademarks. Use them only where the video genuinely has that relationship to claim.`,
};
